import React, { useEffect, useMemo, useState } from 'react'
import { NextPage } from 'next'
import Head from 'next/head'
import {
  Heading,
  Text,
  Stack,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Spinner,
  Button,
  Box,
  Flex,
  Image,
  Grid,
  SelectInput,
  CalendarInput,
} from '@thirstycamel/ui'
import styled from 'styled-components'
import { useAuth } from '../context/AuthContext'
import { getDashboardData } from './api/getDashboardData'
import { useReportingStores } from '../hooks/useReportingStores'
import { getReportingUserDisplayName } from '../hooks/useReportingUser'
import ReportingIncludedStores from '../components/ReportingIncludedStores'
import {
  getReportingSelectionStores,
  ReportingSelectionStore,
} from './api/getReportingSelectionStores'
import ReportingChartCard from '../components/ReportingChartCard'
import ReportingKpiTile from '../components/ReportingKpiTile'
import ReportingTableCard from '../components/ReportingTableCard'
import {
  baseChartOptions,
  baseHorizontalBarOptions,
  basePieOptions,
  buildHorizontalBarData,
  buildPieData,
  buildBarData,
  buildChartData,
  formatCurrencyValue,
  formatDateLabel,
  formatMetricValue,
} from '../utils/reportingChartMapper'
import { useWindowSize } from '../components/ReportingKpiTile'
import {
  PeriodMode,
  PeriodUnit,
  computeDashboardDateRange,
  formatCalendarInputDate,
  isValidDate,
} from '../utils/reportingDateRange'

const Section = styled.div`
  margin-top: 24px;
`

const Index: NextPage = () => {
  const isMobile = useWindowSize()
  const { payload, loading, error, token } = useAuth()
  const [dashboardData, setDashboardData] = useState<any>(null)
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dashboardError, setDashboardError] = useState<string | null>(null)
  const humpClubStoreCode =
    payload && typeof payload === 'object' ? (payload as any)?.store?.humpClubStoreCode : undefined
  const [periodCount, setPeriodCount] = useState('1')
  const [periodUnit, setPeriodUnit] = useState<PeriodUnit>('month')
  const [periodMode, setPeriodMode] = useState<PeriodMode>('last')
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)
  const [selectedStoreType, setSelectedStoreType] = useState<
    'store' | 'region' | 'hotelGroup' | 'storeRep' | undefined
  >('store')
  const [includedStores, setIncludedStores] = useState<ReportingSelectionStore[]>([])
  const [includedStoresLoading, setIncludedStoresLoading] = useState(false)
  const [includedStoresError, setIncludedStoresError] = useState<string | null>(null)
  const [showIncludedStores, setShowIncludedStores] = useState(false)

  const hasToken = Boolean(token)
  const isAdmin = payload?.role === 'ADMIN' || payload?.role === 'HEAD_OFFICE'
  const isStoreRep =
    payload?.isStoreRepMilie === true ||
    payload?.isStoreRepCore === true ||
    payload?.isMultiStoreUser === true
  const loggedInName = getReportingUserDisplayName(
    payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null,
  )

  const {
    stores,
    regions,
    selectedStoreCode,
    setSelectedStoreCode,
    selectedRegion,
    setSelectedRegion,
    hotelGroups,
    selectedHotelGroup,
    setSelectedHotelGroup,
    storeReps,
    selectedStoreRepId,
    setSelectedStoreRepId,
    loadingStores: storesLoading,
    loadingHotelGroups,
    loadingStoreReps,
    error: storesError,
  } = useReportingStores()

  const isHotelGroupFilter = isAdmin && selectedStoreType === 'hotelGroup'
  const isStoreRepFilter = isAdmin && selectedStoreType === 'storeRep'
  const hasEffectiveSelection = useMemo(() => {
    if (!isAdmin && !isStoreRep) {
      return true
    }
    if (selectedStoreType === 'store') {
      return Boolean(selectedStoreCode)
    }
    if (selectedStoreType === 'region') {
      return Boolean(selectedRegion)
    }
    if (selectedStoreType === 'hotelGroup') {
      return Boolean(selectedHotelGroup)
    }
    if (selectedStoreType === 'storeRep') {
      return Boolean(selectedStoreRepId)
    }
    return false
  }, [
    isAdmin,
    isStoreRep,
    selectedStoreType,
    selectedStoreCode,
    selectedRegion,
    selectedHotelGroup,
    selectedStoreRepId,
  ])
  const effectiveSelectionKey = useMemo(() => {
    if (!isAdmin && !isStoreRep) {
      return 'default'
    }
    if (selectedStoreType === 'store') {
      return selectedStoreCode
    }
    if (selectedStoreType === 'region') {
      return selectedRegion
    }
    if (selectedStoreType === 'hotelGroup') {
      return selectedHotelGroup
    }
    if (selectedStoreType === 'storeRep') {
      return selectedStoreRepId
    }
    return ''
  }, [
    isAdmin,
    isStoreRep,
    selectedStoreType,
    selectedStoreCode,
    selectedRegion,
    selectedHotelGroup,
    selectedStoreRepId,
  ])
  const showIncludedStoresPanel =
    isAdmin &&
    ((selectedStoreType === 'region' && Boolean(selectedRegion)) ||
      (isHotelGroupFilter && Boolean(selectedHotelGroup)) ||
      (isStoreRepFilter && Boolean(selectedStoreRepId)))
  const selectedStoreRepName =
    storeReps.find(rep => rep.id === selectedStoreRepId)?.name || selectedStoreRepId
  const isUpdateDisabled =
    storesLoading ||
    loadingHotelGroups ||
    loadingStoreReps ||
    dashboardLoading ||
    (isHotelGroupFilter && !selectedHotelGroup) ||
    (isStoreRepFilter && !selectedStoreRepId)

  // Produces validated API range params from current period controls.
  const computeDateRange = () =>
    computeDashboardDateRange({
      periodMode,
      periodCount,
      periodUnit,
      fromDate,
      toDate,
    })

  const fetchDashboard = async () => {
    setDashboardLoading(true)
    setDashboardError(null)
    try {
      const { startDate, endDate } = computeDateRange()
      const effectiveStoreCode = isAdmin || isStoreRep ? selectedStoreCode : humpClubStoreCode
      const effectiveHotelGroup =
        isHotelGroupFilter && selectedHotelGroup ? selectedHotelGroup : undefined
      const effectiveStoreRepId =
        isStoreRepFilter && selectedStoreRepId ? selectedStoreRepId : undefined
      if (!hasEffectiveSelection) {
        throw new Error('Store, region, hotel group, or store rep selection is required')
      }
      const data = await getDashboardData({
        startDate,
        endDate,
        humpClubStoreCode: selectedStoreType === 'store' ? effectiveStoreCode : undefined,
        region: selectedStoreType === 'region' ? selectedRegion : undefined,
        hotelGroupName: effectiveHotelGroup,
        storeRepId: effectiveStoreRepId,
        periodUnit,
        periodMode,
        token,
      })
      setDashboardData(data.result)
    } catch (err) {
      setDashboardError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setDashboardLoading(false)
    }
  }

  useEffect(() => {
    if (!showIncludedStoresPanel || !token) {
      setIncludedStores([])
      setIncludedStoresError(null)
      return
    }

    let mounted = true

    const loadIncludedStores = async () => {
      setIncludedStoresLoading(true)
      setIncludedStoresError(null)
      try {
        const data = await getReportingSelectionStores(token, {
          region: selectedStoreType === 'region' ? selectedRegion : undefined,
          hotelGroupName: selectedStoreType === 'hotelGroup' ? selectedHotelGroup : undefined,
          storeRepId: selectedStoreType === 'storeRep' ? selectedStoreRepId : undefined,
        })
        if (!mounted) return
        setIncludedStores(data)
      } catch (err) {
        if (!mounted) return
        setIncludedStores([])
        setIncludedStoresError(
          err instanceof Error ? err.message : 'Failed to load included stores',
        )
      } finally {
        if (mounted) {
          setIncludedStoresLoading(false)
        }
      }
    }

    loadIncludedStores()

    return () => {
      mounted = false
    }
  }, [
    showIncludedStoresPanel,
    token,
    selectedStoreType,
    selectedRegion,
    selectedHotelGroup,
    selectedStoreRepId,
  ])

  useEffect(() => {
    const canFetch = hasToken && !loading && !error && hasEffectiveSelection
    if (canFetch) {
      fetchDashboard()
    }
  }, [hasToken, loading, error, hasEffectiveSelection, selectedStoreType, effectiveSelectionKey])

  const metricMap = dashboardData?.metrics ?? {}

  // Controls KPI tile ordering and labels.
  const tiles = useMemo(
    () => [
      { id: 'totalSignUps', label: 'Total Sign Ups' },
      { id: 'totalSignUpsCompleted', label: 'Completed Sign Ups' },
      { id: 'uniqueCustomers', label: 'Unique Customers' },
      { id: 'tagRate', label: 'Tag Rate' },
      { id: 'humpClubRevenue', label: 'Hump Club Revenue' },
      { id: 'transactionCount', label: 'Hump Club Transactions' },
      { id: 'myOffersClaimed', label: 'My Offers Claimed' },
      { id: 'averageBasketValue', label: 'Average Basket Value' },
    ],
    [],
  )

  // Fixed count choices for relative period mode.
  const periodCountOptions = useMemo(
    () =>
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(value => ({
        label: String(value),
        value: String(value),
      })),
    [],
  )

  // Supported relative units for the "Last" mode.
  const periodUnitOptions = useMemo(
    () => [
      { label: 'Week(s)', value: 'week' },
      { label: 'Month(s)', value: 'month' },
      { label: 'Year(s)', value: 'year' },
    ],
    [],
  )

  // Defines the top-level period mode selector values.
  const periodModeOptions = useMemo(
    () => [
      { label: 'Last', value: 'last' },
      { label: 'From', value: 'from' },
    ],
    [],
  )

  // Handles mode switches and resets custom dates when leaving From mode.
  const handlePeriodModeChange = (value: string | number | (string | number)[]) => {
    const nextPeriodMode = value === 'from' ? 'from' : 'last'
    setPeriodMode(nextPeriodMode)
    if (nextPeriodMode === 'last') {
      setFromDate(null)
      setToDate(null)
    }
  }

  // Declares which chart metrics to render and their visual style.
  const chartConfigs = useMemo(
    () => [
      {
        id: 'totalSignUps',
        title: 'Sign Ups by Week',
        type: 'area',
        color: '#acca57',
        integerOnly: true,
      },
      {
        id: 'transactionCount',
        title: 'Transactions by Week',
        type: 'line',
        color: '#acca57',
        integerOnly: true,
      },
      { id: 'humpClubRevenue', title: 'Revenue by Week', type: 'area', color: '#cd1f3d' },
      { id: 'myOffersClaimed', title: 'My Offers Claimed by Week', type: 'bar', color: '#acca57' },
      {
        id: 'averageBasketValue',
        title: 'Avg Basket Value by Week',
        type: 'area',
        color: '#cd1f3d',
      },
      { id: 'tagRateWeekly', title: 'Tag Rate by Week', type: 'line', color: '#acca57' },
    ],
    [],
  )

  // Applies integer y-axis constraints for count-style charts.
  const buildChartOptionsForMetric = (metric: any, integerOnly?: boolean) => {
    if (!integerOnly) {
      return baseChartOptions
    }
    // Uses series values to set a better chart baseline for count metrics.
    const values = (metric?.series ?? []).map((point: any) => point.y).filter(Number.isFinite)
    const minValue = values.length ? Math.min(...values) : 0
    const suggestedMin = minValue > 2 ? Math.floor(minValue - 2) : 0
    return {
      ...baseChartOptions,
      scales: {
        ...baseChartOptions.scales,
        y: {
          ...baseChartOptions.scales?.y,
          suggestedMin,
          ticks: {
            precision: 0,
          },
        },
      },
    }
  }

  const categorySales = dashboardData?.categorySales ?? {}

  const categoryByValue = (categorySales.byCategory ?? []).map((item: any) => ({
    label: item.categoryName,
    value: item.value,
  }))

  const categoryByShare = (categorySales.shareByCategory ?? []).map((item: any) => ({
    label: item.categoryName,
    value: item.percent,
  }))

  const pieColors = [
    '#f9d7de',
    '#f7c3ce',
    '#f3a7b8',
    '#ee879f',
    '#e76583',
    '#de3f69',
    '#cd1f3d',
    '#a8142f',
  ]

  const topProducts = dashboardData?.topProductsByHumpClubSales ?? []
  const loyaltyCustomers = dashboardData?.top10LoyaltyCustomers ?? []
  const reimbursementData = dashboardData?.reimbursementData ?? []
  const reimbursementTotals = dashboardData?.reimbursementTotals ?? {}
  const [reimbursementPage, setReimbursementPage] = useState(1)
  const REIMBURSEMENT_PAGE_SIZE = 10
  const reimbursementPages = Math.max(
    1,
    Math.ceil(reimbursementData.length / REIMBURSEMENT_PAGE_SIZE),
  )
  const paginatedReimbursement = useMemo(
    () =>
      reimbursementData.slice(
        (reimbursementPage - 1) * REIMBURSEMENT_PAGE_SIZE,
        reimbursementPage * REIMBURSEMENT_PAGE_SIZE,
      ),
    [reimbursementData, reimbursementPage],
  )

  // Reset to page 1 when reimbursement data changes
  useEffect(() => {
    setReimbursementPage(1)
  }, [reimbursementData.length])

  return (
    <Box>
      <Head>
        <title>Thirsty Camel - Reporting</title>
      </Head>
      <Stack>
        {!hasToken && (
          <Alert status="warning" variant="left-accent">
            <AlertIcon />
            <Stack spacing={1}>
              <AlertTitle>No token detected</AlertTitle>
              <AlertDescription>
                Launch Reporting from the Thirsty Camel Portal or Milie.
              </AlertDescription>
            </Stack>
          </Alert>
        )}

        {hasToken && loading && (
          <Stack direction="row" alignItems="center" spacing={3}>
            <Spinner size="sm" />
            <Text color="gray.600">Validating token…</Text>
          </Stack>
        )}

        {hasToken && error && (
          <Alert status="error" variant="left-accent">
            <AlertIcon />
            <Stack spacing={1}>
              <AlertTitle>Token could not be validated</AlertTitle>
              <AlertDescription>
                Ensure a valid JWT is provided in the pushToken query parameter or session storage.
              </AlertDescription>
            </Stack>
          </Alert>
        )}
      </Stack>
      <Stack>
        <Flex
          direction={['column', null, 'row']}
          alignItems={['flex-start', null, 'center']}
          justifyContent="space-between"
          backgroundColor="#acca57"
          p={4}
          color="white"
          style={{ marginBottom: '0px' }}
          width="100%"
          gridGap={3}
        >
          <Flex direction="row" alignItems="center" width={['100%', null, 'auto']} minWidth={0}>
            <Image src="/static/images/logo.png" alt="Camel" height={50} />
            <Heading fontSize={['xl', null, '2xl']} ml={4} lineHeight="shorter">
              {dashboardData?.meta?.storeName}
            </Heading>
          </Flex>
          <Flex
            direction={['column', null, 'row']}
            alignItems={['center', null, 'center']}
            gridGap={[1, null, 4]}
            width={['100%', null, 'auto']}
            textAlign={isMobile ? 'center' : 'right'}
          >
            {loggedInName && (
              <Text
                color="white"
                fontSize={['sm', null, 'lg']}
                fontWeight={isMobile ? '600' : '400'}
                whiteSpace="nowrap"
              >
                Logged in as: {loggedInName}
              </Text>
            )}
            {dashboardData?.meta?.startDate && dashboardData?.meta?.endDate && (
              <Text
                color="white"
                fontSize={['sm', null, 'lg']}
                fontWeight={isMobile ? '600' : '400'}
                whiteSpace="nowrap"
              >
                Reporting Period: {formatDateLabel(dashboardData.meta.startDate)} -{' '}
                {formatDateLabel(dashboardData.meta.endDate)}
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex backgroundColor="#cd1f3d" height="25px" mt={0} />
        <Box px={3}>
          <Stack spacing={3} mt={3}>
            <Flex
              direction={['column', null, 'row']}
              alignItems={['stretch', null, 'center']}
              gridGap={3}
              width="100%"
            >
              <Flex
                direction={['column', null, 'row']}
                alignItems={['stretch', null, 'center']}
                gridGap={3}
                flex="1"
                minWidth={0}
              >
                <Flex direction="row" alignItems="center" gridGap={2} width="100%" minWidth={0}>
                  <Box flex="1" minWidth={0} maxWidth={[null, null, '120px']}>
                    <SelectInput
                      value={periodMode}
                      options={periodModeOptions}
                      onChangeValue={handlePeriodModeChange}
                      height="40px"
                    />
                  </Box>

                  {periodMode === 'last' && (
                    <>
                      <Box flex="1" minWidth={0} maxWidth={[null, null, '120px']}>
                        <SelectInput
                          value={periodCount}
                          options={periodCountOptions}
                          onChangeValue={value => setPeriodCount(String(value))}
                          height="40px"
                        />
                      </Box>
                      <Box flex="1" minWidth={0} maxWidth={[null, null, '160px']}>
                        <SelectInput
                          value={periodUnit}
                          options={periodUnitOptions}
                          onChangeValue={value => setPeriodUnit(value as PeriodUnit)}
                          height="40px"
                        />
                      </Box>
                    </>
                  )}

                  {periodMode === 'from' && (
                    <>
                      <Text whiteSpace="nowrap">From</Text>
                      <Box flex="1" minWidth={0} maxWidth={[null, null, '180px']}>
                        <CalendarInput
                          value={fromDate}
                          onChangeValue={(value: any) =>
                            setFromDate(isValidDate(value) ? value : null)
                          }
                          clickUnselectsDay
                          placeholder="Select date"
                          format="DD/MM/YY"
                          formatDate={formatCalendarInputDate}
                          height="40px"
                        />
                      </Box>
                      <Text whiteSpace="nowrap">To</Text>
                      <Box flex="1" minWidth={0} maxWidth={[null, null, '180px']}>
                        <CalendarInput
                          value={toDate}
                          onChangeValue={(value: any) =>
                            setToDate(isValidDate(value) ? value : null)
                          }
                          clickUnselectsDay
                          placeholder="Select date"
                          format="DD/MM/YY"
                          formatDate={formatCalendarInputDate}
                          height="40px"
                        />
                      </Box>
                    </>
                  )}
                </Flex>

                {isAdmin && (
                  <Flex direction="row" alignItems="center" gridGap={2} width="100%" minWidth={0}>
                    <Box flex="1" minWidth={0} maxWidth={[null, null, '360px']}>
                      <SelectInput
                        value={selectedStoreType}
                        options={[
                          { label: 'Store', value: 'store' as const },
                          ...(payload?.role === 'ADMIN'
                            ? [{ label: 'Region', value: 'region' as const }]
                            : []),
                          { label: 'Hotel Group', value: 'hotelGroup' as const },
                          { label: 'Store Rep', value: 'storeRep' as const },
                        ]}
                        onChangeValue={value =>
                          setSelectedStoreType(
                            value as 'store' | 'region' | 'hotelGroup' | 'storeRep' | undefined,
                          )
                        }
                        height="40px"
                      />
                    </Box>
                  </Flex>
                )}

                {(isAdmin || isStoreRep) && selectedStoreType === 'store' && (
                  <Flex direction="row" alignItems="center" gridGap={2} width="100%" minWidth={0}>
                    <Text whiteSpace="nowrap">Store</Text>
                    <Box flex="1" minWidth={0} maxWidth={[null, null, '360px']}>
                      <SelectInput
                        placeholder="Select store"
                        value={selectedStoreCode || ''}
                        options={stores.map(store => ({
                          label: `${store.storeName}${store.region ? ` (${store.region})` : ''}`,
                          value: store.humpClubStoreCode,
                        }))}
                        onChangeValue={(value: any) => {
                          setSelectedRegion('')
                          setSelectedHotelGroup('')
                          setSelectedStoreRepId('')
                          setSelectedStoreCode(String(value))
                        }}
                        height="40px"
                      />
                    </Box>
                  </Flex>
                )}

                {isAdmin && selectedStoreType === 'region' && (
                  <Flex direction="row" alignItems="center" gridGap={2} width="100%" minWidth={0}>
                    <Text whiteSpace="nowrap">Region</Text>
                    <Box flex="1" minWidth={0} maxWidth={[null, null, '360px']}>
                      <SelectInput
                        placeholder="Select region"
                        value={selectedRegion || ''}
                        options={regions.map(region => ({
                          label: region,
                          value: region,
                        }))}
                        onChangeValue={(value: any) => {
                          setSelectedStoreCode('')
                          setSelectedHotelGroup('')
                          setSelectedStoreRepId('')
                          setSelectedRegion(String(value))
                        }}
                        height="40px"
                      />
                    </Box>
                  </Flex>
                )}

                {isHotelGroupFilter && (
                  <Flex direction="row" alignItems="center" gridGap={2} width="100%" minWidth={0}>
                    <Text whiteSpace="nowrap">Hotel Group</Text>
                    <Box flex="1" minWidth={0} maxWidth={[null, null, '360px']}>
                      <SelectInput
                        placeholder={
                          loadingHotelGroups
                            ? 'Loading hotel groups…'
                            : hotelGroups.length
                            ? 'Select hotel group'
                            : 'No hotel groups available'
                        }
                        value={selectedHotelGroup || ''}
                        options={hotelGroups.map(group => ({
                          label: group,
                          value: group,
                        }))}
                        onChangeValue={(value: any) => {
                          setSelectedStoreCode('')
                          setSelectedRegion('')
                          setSelectedStoreRepId('')
                          setSelectedHotelGroup(String(value))
                        }}
                        height="40px"
                      />
                    </Box>
                  </Flex>
                )}

                {isStoreRepFilter && (
                  <Flex direction="row" alignItems="center" gridGap={2} width="100%" minWidth={0}>
                    <Text whiteSpace="nowrap">Store Rep</Text>
                    <Box flex="1" minWidth={0} maxWidth={[null, null, '360px']}>
                      <SelectInput
                        placeholder={
                          loadingStoreReps
                            ? 'Loading store reps…'
                            : storeReps.length
                            ? 'Select store rep'
                            : 'No store reps available'
                        }
                        value={selectedStoreRepId || ''}
                        options={storeReps.map(rep => ({
                          label: rep.email ? `${rep.name} (${rep.email})` : rep.name,
                          value: rep.id,
                        }))}
                        onChangeValue={(value: any) => {
                          setSelectedStoreCode('')
                          setSelectedRegion('')
                          setSelectedHotelGroup('')
                          setSelectedStoreRepId(String(value))
                        }}
                        height="40px"
                      />
                    </Box>
                  </Flex>
                )}
              </Flex>

              <Button
                onClick={fetchDashboard}
                variantColor="blue"
                height="40px"
                width={['100%', null, '120px']}
                isDisabled={isUpdateDisabled}
              >
                Update
              </Button>
            </Flex>

            {(storesLoading ||
              loadingHotelGroups ||
              loadingStoreReps ||
              storesError ||
              dashboardLoading) && (
              <Stack spacing={1}>
                {storesLoading && (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Spinner size="sm" />
                    <Text color="gray.600">Loading stores…</Text>
                  </Stack>
                )}
                {loadingHotelGroups && (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Spinner size="sm" />
                    <Text color="gray.600">Loading hotel groups…</Text>
                  </Stack>
                )}
                {loadingStoreReps && (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Spinner size="sm" />
                    <Text color="gray.600">Loading store reps…</Text>
                  </Stack>
                )}
                {storesError && <Text color="red">{storesError}</Text>}
                {dashboardLoading && (
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Spinner size="sm" />
                    <Text color="gray.600">Loading dashboard data…</Text>
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
          {showIncludedStoresPanel && (
            <ReportingIncludedStores
              title={
                selectedStoreType === 'region'
                  ? `Stores included in region: ${selectedRegion}`
                  : selectedStoreType === 'storeRep'
                  ? `Stores included for store rep: ${selectedStoreRepName}`
                  : `Stores included in hotel group: ${selectedHotelGroup}`
              }
              stores={includedStores}
              loading={includedStoresLoading}
              error={includedStoresError}
              expanded={showIncludedStores}
              onToggle={() => setShowIncludedStores(current => !current)}
            />
          )}
          {dashboardError && (
            <Box pt={4}>
              <Alert status="error" variant="left-accent">
                <AlertIcon />
                <Stack spacing={1}>
                  <AlertTitle>Could not load dashboard data</AlertTitle>
                  <AlertDescription>{dashboardError}</AlertDescription>
                </Stack>
              </Alert>
            </Box>
          )}
          {dashboardData && (
            <Stack spacing={6} mt={6}>
              <Section style={{ marginTop: '0px' }}>
                <Grid
                  gridTemplateColumns={[
                    'repeat(2, minmax(0, 1fr))',
                    null,
                    'repeat(4, minmax(180px, 1fr))',
                  ]}
                  gridGap={[3, null, 4]}
                >
                  {tiles.map(tile => {
                    const metric = metricMap[tile.id]
                    return (
                      <ReportingKpiTile
                        key={tile.id}
                        label={tile.label}
                        value={formatMetricValue(metric?.total ?? 0, metric?.unit)}
                      />
                    )
                  })}
                </Grid>
              </Section>

              <Section>
                <Grid
                  gridTemplateColumns={['1fr', null, 'repeat(3, minmax(240px, 1fr))']}
                  gridGap={[3, null, 4]}
                >
                  {chartConfigs.map(config => {
                    const metric = metricMap[config.id]
                    if (!metric?.series?.length) {
                      return null
                    }
                    const data =
                      config.type === 'bar'
                        ? buildBarData(metric, config.color)
                        : buildChartData(metric, config.color, config.type === 'area')
                    return (
                      <ReportingChartCard
                        key={config.id}
                        title={config.title}
                        chartType={config.type as 'line' | 'area' | 'bar' | 'pie'}
                        data={data}
                        options={buildChartOptionsForMetric(metric, config.integerOnly) as unknown}
                      />
                    )
                  })}
                </Grid>
              </Section>

              <Section>
                <Grid
                  gridTemplateColumns={['1fr', null, 'repeat(2, minmax(240px, 1fr))']}
                  gridGap={[3, null, 4]}
                >
                  <ReportingChartCard
                    title="Hump Club Sales by Category ($)"
                    chartType="bar"
                    data={buildHorizontalBarData(categoryByValue, '#acca57')}
                    options={baseHorizontalBarOptions}
                  />
                  <ReportingChartCard
                    title="Hump Club Sales Share by Category (%)"
                    chartType="pie"
                    data={buildPieData(categoryByShare, pieColors)}
                    options={basePieOptions}
                  />
                </Grid>
              </Section>

              <Section>
                <Grid
                  gridTemplateColumns={['1fr', null, 'repeat(2, minmax(240px, 1fr))']}
                  gridGap={[3, null, 4]}
                >
                  <ReportingTableCard
                    title="Top Products by Hump Club Sales ($)"
                    rows={topProducts}
                    columns={[
                      { header: 'Product Name', key: 'productName' },
                      { header: 'Pack Size', key: 'packSize' },
                      { header: 'Qty Sold', key: 'qtySold' },
                      {
                        header: 'Value $',
                        key: 'value',
                        format: value => formatCurrencyValue(Number(value || 0)),
                      },
                    ]}
                  />
                  <ReportingTableCard
                    title="Top 10 Loyalty Customers"
                    rows={loyaltyCustomers}
                    columns={[
                      { header: 'Name', key: 'fullName' },
                      {
                        header: 'Period Spend',
                        key: 'totalSpent',
                        format: value => formatCurrencyValue(Number(value || 0)),
                      },
                      {
                        header: 'Lifetime Value',
                        key: 'lifetimeTotalSpent',
                        format: value => formatCurrencyValue(Number(value || 0)),
                      },
                      {
                        header: 'Date Joined',
                        key: 'dateJoined',
                        format: value => formatDateLabel(String(value || '')),
                      },
                    ]}
                  />
                </Grid>
              </Section>
              <Section>
                <Stack spacing={3}>
                  <Grid
                    gridTemplateColumns={[
                      'repeat(2, minmax(0, 1fr))',
                      null,
                      'repeat(2, minmax(180px, 1fr))',
                    ]}
                    gridGap={[3, null, 4]}
                  >
                    <ReportingKpiTile
                      label="Total Offers Redeemed"
                      value={formatMetricValue(
                        reimbursementTotals.totalOffersRedeemed ?? 0,
                        'count',
                      )}
                    />
                    <ReportingKpiTile
                      label="Total Reimbursement Amount"
                      value={formatMetricValue(
                        reimbursementTotals.totalReimbursementAmount ?? 0,
                        'currency',
                      )}
                    />
                  </Grid>
                  <ReportingTableCard
                    title="Reimbursements"
                    rows={paginatedReimbursement}
                    columns={[
                      { header: 'Offer Name', key: 'offerName' },
                      {
                        header: 'Offer Value',
                        key: 'offerValue',
                        format: value => formatCurrencyValue(Number(value || 0)),
                      },
                      { header: 'Total Redemptions', key: 'totalRedemptions' },
                      {
                        header: 'Total Reimbursement Amount',
                        key: 'totalDiscountAmount',
                        format: value => formatCurrencyValue(Number(value || 0)),
                      },
                    ]}
                  />
                  {reimbursementData.length > REIMBURSEMENT_PAGE_SIZE && (
                    <Flex direction="row" alignItems="center" justifyContent="space-between" px={2}>
                      {!isMobile && (
                        <Text fontSize="sm" color="gray.600">
                          Showing {(reimbursementPage - 1) * REIMBURSEMENT_PAGE_SIZE + 1}–
                          {Math.min(
                            reimbursementPage * REIMBURSEMENT_PAGE_SIZE,
                            reimbursementData.length,
                          )}{' '}
                          of {reimbursementData.length} offers
                        </Text>
                      )}
                      <Flex direction="row" gridGap={2} paddingTop={2} paddingBottom={6}>
                        <Button
                          size="md"
                          variantColor="blue"
                          onClick={() => setReimbursementPage(p => Math.max(1, p - 1))}
                          isDisabled={reimbursementPage <= 1}
                        >
                          Previous
                        </Button>
                        <Button
                          size="md"
                          variantColor="blue"
                          onClick={() =>
                            setReimbursementPage(p => Math.min(reimbursementPages, p + 1))
                          }
                          isDisabled={reimbursementPage >= reimbursementPages}
                        >
                          Next
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </Stack>
              </Section>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

export default Index
